// Age bands for reports (docs/plans/phase1-reports.md §6, question R2).
//
// A band is data taken from a form's instructions, never a constant here. Its bounds are
// completed ages on a given calendar day: "under 5 years" is `{ from: null, below: 5
// years }`, so a child is in it up to the day before the fifth birthday. Completed months
// and years use the end-of-month clamp in time.ts (a 29 February birthday falls on
// 28 February in a common year), so a banner and a report never disagree about an age.
//
// TODO(verify-spec): the official 705A/705B boundary and the day age is measured on come
// from the MOH reporting instructions (R2). Until then only synthetic bands are used.

import type { CalendarDay } from '../time.ts';
import { addDays, completedMonths, daysBetween } from '../time.ts';

export const AGE_BOUND_UNITS = ['days', 'months', 'years'] as const;
export type AgeBoundUnit = (typeof AGE_BOUND_UNITS)[number];

export interface AgeBound {
  readonly unit: AgeBoundUnit;
  readonly value: number;
}

/** `from` is included, `below` is not. Null means no limit on that side. */
export interface AgeBand {
  readonly from: AgeBound | null;
  readonly below: AgeBound | null;
}

/** Completed days, months or years on a day. Null when the birth date is after the day. */
export function completedAge(
  birthDate: CalendarDay,
  on: CalendarDay,
  unit: AgeBoundUnit,
): number | null {
  const days = daysBetween(birthDate, on);
  if (days < 0) return null;
  if (unit === 'days') return days;
  const months = completedMonths(birthDate, on);
  return unit === 'months' ? months : Math.floor(months / 12);
}

function atLeast(birthDate: CalendarDay, on: CalendarDay, bound: AgeBound): boolean {
  const age = completedAge(birthDate, on, bound.unit);
  return age !== null && age >= bound.value;
}

/** Whether someone born on `birthDate` is in the band on `on`. False before birth. */
export function inAgeBand(birthDate: CalendarDay, on: CalendarDay, band: AgeBand): boolean {
  if (daysBetween(birthDate, on) < 0) return false;
  if (band.from !== null && !atLeast(birthDate, on, band.from)) return false;
  if (band.below !== null && atLeast(birthDate, on, band.below)) return false;
  return true;
}

/**
 * Whether an estimated birth date that is off by up to `marginDays` either way could put
 * the person in a different band. Reports list these for review instead of trusting them.
 */
export function bandDependsOnEstimate(
  birthDate: CalendarDay,
  on: CalendarDay,
  band: AgeBand,
  marginDays: number,
): boolean {
  const actual = inAgeBand(birthDate, on, band);
  return (
    inAgeBand(addDays(birthDate, -marginDays), on, band) !== actual ||
    inAgeBand(addDays(birthDate, marginDays), on, band) !== actual
  );
}
